import { DurationString, isAbsoluteTimeRange, isRelativeTimeRange, TimeRangeValue } from '@chronosphereio/core';
import { z } from 'zod/v4';

// TODO: use regex to validate duration
export const durationSchema = z.string();

export const timeOptionSchema = z.enum(['default', 'monthly']);

export type NamedTimeOption = z.infer<typeof timeOptionSchema>;

export const timeDashboardSchema = z.object({
  /**
   * Name time options for predefined custom ranges like 'monthly'.
   */
  options: timeOptionSchema.optional(),
  /**
   * Absolute start time, when defined this takes priority over saved 'duration'.
   */
  start: z.string().optional(),
  /**
   * Absolute end time, must be defined with 'start' time to be valid.
   */
  end: z.string().optional(),
});

export interface DashboardTimeDefinition extends z.TypeOf<typeof timeDashboardSchema> {}

export interface TimeOption {
  value: TimeRangeValue;
  display?: string;
}

/**
 * Persists a time range to a dashboard spec, clearing the opposite field
 * to ensure only duration (relative) or time (absolute) is set.
 */
export function applyTimeRangeToSpec(
  spec: { duration?: DurationString; time?: DashboardTimeDefinition },
  timeRange: TimeRangeValue
): void {
  if (isRelativeTimeRange(timeRange)) {
    spec.duration = timeRange.pastDuration;
    if (spec.time !== undefined) {
      delete spec.time;
    }
  } else if (isAbsoluteTimeRange(timeRange)) {
    spec.time = {
      start: timeRange.start.toISOString(),
      end: timeRange.end.toISOString(),
    };
    if (spec.duration !== undefined) {
      delete spec.duration;
    }
  }
}

/**
 * Converts a TimeRangeValue to a DashboardTimeDefinition
 */
export function timeRangeToDashboardTime(timeRange: TimeRangeValue): DashboardTimeDefinition | undefined {
  if (isRelativeTimeRange(timeRange)) {
    return { start: timeRange.pastDuration };
  }
  if (isAbsoluteTimeRange(timeRange)) {
    return { start: timeRange.start.toISOString(), end: timeRange.end.toISOString() };
  }
  return undefined;
}
